/* eslint-disable max-lines */
import { customAlphabet } from 'nanoid';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import PRODUCT_TYPE from 'Component/Product/Product.config';
import ProductList from 'Component/ProductList';
// import ProductListPage from 'Component/ProductListPage';
import ProductListQuery from 'Query/ProductList.query';
import ProductListPriceQuery from 'Query/ProductListPrice.query';
import ProductListVariantsQuery from 'Query/ProductListVariants.query';
import {
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps,
    ProductListWidgetContainer as SourceProductListWidgetContainer
} from 'SourceComponent/ProductListWidget/ProductListWidget.container';
import { FilterType } from 'Type/Category.type';
import { MixType } from 'Type/Common.type';
import { DeviceType } from 'Type/Device.type';
import { PagesType } from 'Type/ProductList.type';
import { noopFn } from 'Util/Common';
import componentLoader from 'Util/componentLoader';
import { getIndexedProducts } from 'Util/Product';
import { makeCancelable } from 'Util/Promise';
import { prepareQuery } from 'Util/Query';
import { executeGet } from 'Util/Request';
import { hash } from 'Util/Request/Hash';
import { ONE_MONTH_IN_SECONDS } from 'Util/Request/QueryDispatcher';
import { mergeProducts } from 'Util/Variants';

export const ProductListDispatcher = componentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/ProductList/ProductList.dispatcher'
), 2);

/** @namespace Bodypwa/Component/ProductListWidget/Container/mapStateToProps */
export const mapStateToProps = () => ({
    ...sourceMapStateToProps()
});
/** @namespace Bodypwa/Component/ProductListWidget/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch)
});
// TODO create config for widget
// eslint-disable-next-line @scandipwa/scandipwa-guidelines/create-config-files
export const idPattern = '123456';

// eslint-disable-next-line no-magic-numbers
export const nanoid = customAlphabet(idPattern, 10);

/** @namespace Bodypwa/Component/ProductListWidget/Container */
export class ProductListWidgetContainer extends SourceProductListWidgetContainer {
    static propTypes = {
        device: DeviceType.isRequired,
        title: PropTypes.string,
        pages: PagesType.isRequired,
        selectedFilters: FilterType,
        isLoading: PropTypes.bool,
        updatePage: PropTypes.func,
        totalPages: PropTypes.number,
        loadPage: PropTypes.func,
        loadPrevPage: PropTypes.func,
        isShowLoading: PropTypes.bool,
        isVisible: PropTypes.bool,
        isInfiniteLoaderEnabled: PropTypes.bool,
        isPaginationEnabled: PropTypes.bool,
        isWidget: PropTypes.bool,
        mix: MixType,
        isSwiper: PropTypes.bool
    };

    static defaultProps = {
        mix: {},
        title: '',
        isInfiniteLoaderEnabled: false,
        isPaginationEnabled: false,
        selectedFilters: {},
        isLoading: false,
        updatePage: noopFn,
        totalPages: 1,
        loadPage: noopFn,
        loadPrevPage: noopFn,
        isShowLoading: false,
        isVisible: true,
        isWidget: false,
        isSwiper: false
    };

    state = {
        pages: {},
        totalItems: 0,
        totalPages: 0,
        isLoading: true,
        sliderId: 0
    };

    updateProducts = this.updateProducts.bind(this);

    containerProps() {
        const {
            device,
            isShowLoading,
            isVisible,
            loadPrevPage,
            requestPage,
            selectedFilters,
            title,
            updatePage
        } = this.props;
        const {
            pages,
            totalItems,
            totalPages,
            isLoading
        } = this.state;

        return {
            device,
            isShowLoading,
            isVisible,
            loadPrevPage,
            requestPage,
            selectedFilters,
            title,
            updatePage,
            pages,
            totalItems,
            totalPages,
            isLoading,
            ...this.adaptProps()
        };
    }

    componentWillMount() {
        this.setState({
            sliderId: nanoid()
        });
    }

    requestProductList(options) {
        const { isNext } = options;
        const { sliderId } = this.props;
        if (!isNext) {
            this.updateLoadStatus(true);
        }

        this.fetchData(
            [ProductListQuery.getQuery({ isWidget: true, ...options })],
            this.updateProductListItems,
            this.onError,
            `ProductListQuery${ sliderId }`
        );
    }

    updateProducts(data, additionalProducts) {
        const { pages, sliderId } = this.state;
        const {
            products: {
                items
            } = {}
        } = data;

        if (pages[sliderId] || additionalProducts) {
            const { mergedItems } = mergeProducts(pages[sliderId] || additionalProducts, getIndexedProducts(items));
            this.setState({
                isPageLoading: false,
                pages: {
                    ...pages,
                    [sliderId]: mergedItems
                }
            });
        }
    }

    appendPage(data) {
        const { showPager } = this.props;
        const { pages, sliderId } = this.state;
        const {
            products: {
                items
            } = {}
        } = data;

        if (showPager === 0) {
            return;
        }
        if (pages[sliderId]) {
            const { mergedItems } = mergeProducts(pages[sliderId], getIndexedProducts(items));
            this.setState({
                isPageLoading: false,
                pages: {
                    ...pages,
                    [sliderId]: mergedItems
                }
            });
        } else {
            const itemsWithVariants = [];
            items.filter((item) => {
                const { attributes } = item;
                const filteredAttributes = attributes.filter((attr) => attr.update_product_preview_image && item.type_id
                === PRODUCT_TYPE.configurable && attr.attribute_options.length > 1);

                if (filteredAttributes.length) {
                    itemsWithVariants.push(item.sku);
                }

                return item;
            });
            this.fetchData(
                [ProductListVariantsQuery.getQuery({
                    args: {
                        filter: { productsSkuArray: itemsWithVariants }
                    },
                    isPdp: false
                })],
                this.appendPage,
                this.onError
            );
            this.setState({
                pages: {
                    ...pages,
                    [sliderId]: getIndexedProducts(items)
                }
            });
        }
    }

    updateProductListItems(data) {
        const { productsCount, productsPerPage } = this.props;
        const { sliderId } = this.state;
        const {
            products: {
                items,
                total_count: totalItems
            } = {}
        } = data;
        const originalProducts = getIndexedProducts(items);
        const totalPages = Math.ceil(productsCount / productsPerPage);
        this.setState({
            isLoading: false,
            totalItems,
            totalPages,
            sliderId,
            pages: { [sliderId]: originalProducts }
        });
        const itemsWithVariants = [];
        const itemsWithPrice = [];
        items.filter((item) => {
            const { attributes } = item;
            const filteredAttributes = attributes?.filter((attr) => attr.update_product_preview_image
                && item.type_id
                === PRODUCT_TYPE.configurable && attr.attribute_options.length > 1);

            if (filteredAttributes?.length) {
                itemsWithVariants.push(item.sku);
            }

            return item;
        });
        items.filter((item) => itemsWithPrice.push(item.sku));
        this.fetchData(
            [ProductListVariantsQuery.getQuery({
                args: {
                    filter: { productsSkuArray: itemsWithVariants }
                },
                isPdp: false
            })],
            this.updateProducts,
            this.onError,
            `ProductListVariantsQuery${ sliderId}`,
            originalProducts
        );
        this.fetchData(
            [ProductListPriceQuery.getQuery({
                args: {
                    filter: { productsSkuArray: itemsWithPrice }
                },
                isPdp: false
            })],
            this.updateProducts,
            this.onError,
            `ProductListPriceQuery${ sliderId}`,
            originalProducts
        );
    }

    fetchData(
        rawQueries, onSuccess = noopFn, onError = noopFn, broadcastName = 'ProductListQuery', additionalProducts
    ) {
        const preparedQuery = prepareQuery(rawQueries);
        const { query, variables } = preparedQuery;
        const queryHash = hash(query + JSON.stringify(variables));

        if (!window.dataCache) {
            window.dataCache = {};
        }

        if (window.dataCache[queryHash]) {
            onSuccess(window.dataCache[queryHash], additionalProducts);

            return;
        }

        this.promise = makeCancelable(
            executeGet(preparedQuery, broadcastName, ONE_MONTH_IN_SECONDS)
        );

        this.promise.promise.then(
            /** @namespace Bodypwa/Component/ProductListWidget/Container/ProductListWidgetContainer/fetchData/then */
            (response) => {
                window.dataCache[queryHash] = response;
                onSuccess(response, additionalProducts);
            },
            /** @namespace Bodypwa/Component/ProductListWidget/Container/ProductListWidgetContainer/fetchData/then/onError/catch */
            (err) => onError(err)
        );
    }

    render() {
        const { sliderId } = this.state;
        return (
            <ProductList
              { ...this.containerProps() }
              { ...this.containerFunctions }
              sliderId={ sliderId }
              isInfiniteLoaderEnabled={ false }
              numberOfPlaceholders={ 6 }
              isSwiper
              mix={ { block: 'ProductListWidget' } }
              isWidget
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductListWidgetContainer);
